import { DATE_FORMAT_YEAR_MONTH_DAY, getCurrentDateObject, getDateFormatted } from '@marriott/mi-ui-library';

const DEFAULT_GUEST_ROOMS = '10';
const URL_PLACEHOLDER = '{url}';

export const appendSearchCriteriaToCtaLink = (ctaLink?: string, destination?: string): string | undefined => {
  if (!ctaLink) {
    return ctaLink;
  }

  if (!ctaLink.includes(URL_PLACEHOLDER)) {
    return ctaLink;
  }

  const [pathAndQuery, hash] = ctaLink.split('#');
  const [path, existingQuery = ''] = pathAndQuery.split('?');
  const params = new URLSearchParams(existingQuery.replace(URL_PLACEHOLDER, ''));
  params.delete(URL_PLACEHOLDER);
  params.delete('');

  if (!params.get('startDate')) {
    params.set('startDate', getDateFormatted(getCurrentDateObject(), DATE_FORMAT_YEAR_MONTH_DAY));
  }
  if (!params.get('guestRooms')) {
    params.set('guestRooms', DEFAULT_GUEST_ROOMS);
  }
  if (!params.get('destination') && destination) {
    params.set('destination', destination);
  }

  const search = params.toString();
  return `${path}${search ? `?${search}` : ''}${hash ? `#${hash}` : ''}`;
};

const getCardDestination = (item: Record<string, unknown>): string | undefined => {
  const destination = item['destination'] ?? item['header'];
  return typeof destination === 'string' ? destination : undefined;
};

const patchCardItemCtaLink = (item: Record<string, unknown>) => {
  if (typeof item?.['ctaLink'] !== 'string') {
    return item;
  }
  return {
    ...item,
    ctaLink: appendSearchCriteriaToCtaLink(item['ctaLink'], getCardDestination(item)),
  };
};

const patchNestedCardItems = (sourceItems: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(sourceItems).map(([cardName, card]) => {
      if (!card || typeof card !== 'object') {
        return [cardName, card];
      }
      const cardRecord = card as Record<string, unknown>;
      const nestedItems = cardRecord[':items'] ?? cardRecord['cqItems'];
      if (!nestedItems || typeof nestedItems !== 'object') {
        return [cardName, card];
      }
      const patchedNestedItems = Object.fromEntries(
        Object.entries(nestedItems as Record<string, unknown>).map(([itemKey, item]) => [
          itemKey,
          item && typeof item === 'object' ? patchCardItemCtaLink(item as Record<string, unknown>) : item,
        ])
      );
      return [
        cardName,
        {
          ...cardRecord,
          ...(cardRecord[':items'] ? { ':items': patchedNestedItems } : {}),
          ...(cardRecord['cqItems'] ? { cqItems: patchedNestedItems } : {}),
        },
      ];
    })
  );

const pickItemMap = (...candidates: unknown[]): Record<string, unknown> | undefined =>
  candidates.find(
    (value): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value)
  );

/**
 * AWP CardCarouselContainer reads flattened cqItems (not model) for nested card CTAs.
 * Flatten model fields onto props and overlay patched cqItems; leave the original model object untouched.
 */
export const withSearchCriteriaOnCarouselProps = <T extends Record<string, unknown>>(props: T): T => {
  const model = props['model'] as Record<string, unknown> | undefined;
  const flattened = {
    ...props,
    ...(model ?? {}),
    isAuthorMode: props['isAuthorMode'] ?? model?.['isAuthorMode'],
  } as Record<string, unknown>;

  const sourceItems = pickItemMap(flattened['cqItems'], flattened[':items'], model?.['cqItems'], model?.[':items']);
  if (!sourceItems) {
    return flattened as unknown as T;
  }

  const patchedItems = patchNestedCardItems(sourceItems);
  return {
    ...flattened,
    cqItems: patchedItems,
    ':items': patchedItems,
    model,
  } as unknown as T;
};
